import React, { useState, useEffect } from 'react';
import { Download, Plus, Trash2, RefreshCw, XCircle, AlertCircle, Star } from 'lucide-react';
import { qrCodeManagementService, QRCode, QRCodeStats } from '../../services/qrCodeManagementService';

import QRCodeKPICards from './components/QRCodeKPICards';
import QRCodeDataTable from './components/QRCodeDataTable';
import BulkCreateModal from './components/BulkCreateModal';
import PermanentQRCodeModal from './components/PermanentQRCodeModal';
import PermanentQRCodeTable from './components/PermanentQRCodeTable';
import { Helmet } from 'react-helmet';

const QRCodeManagement: React.FC = () => {
  const [qrCodes, setQrCodes] = useState<QRCode[]>([]);
  const [stats, setStats] = useState<QRCodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
  const [filterStatus, setFilterStatus] = useState<'all' | 'active' | 'redeemed' | 'expired'>('all');
  const [filterHotel, setFilterHotel] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showBulkCreateModal, setShowBulkCreateModal] = useState(false);
  const [showPermanentModal, setShowPermanentModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'temporary' | 'permanent'>('temporary');
  
  // Pagination states
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [tempItemsPerPage, setTempItemsPerPage] = useState(20);
  const [tempCurrentPage, setTempCurrentPage] = useState(1);

  useEffect(() => {
    loadQRCodeData();
  }, []);

  const loadQRCodeData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [codesData, statsData] = await Promise.all([
        qrCodeManagementService.getAllQRCodes(),
        qrCodeManagementService.getQRCodeStats()
      ]);

      setQrCodes(codesData);
      setStats(statsData);
    } catch (err) {
      console.error('Error loading QR code data:', err);
      setError('Failed to load QR code data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreate = async (hotelName: string, count: number, expiresInDays?: number) => {
    try {
      setLoading(true);
      await qrCodeManagementService.bulkCreateQRCodes({ hotelName, count, expiresInDays });
      await loadQRCodeData();
      setShowBulkCreateModal(false);
    } catch (error) {
      console.error('Error creating QR codes:', error);
      setError('Failed to create QR codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedCodes.length === 0) return;
    
    if (!window.confirm(`Are you sure you want to delete ${selectedCodes.length} QR code(s)?`)) {
      return;
    }

    try {
      setLoading(true);
      await qrCodeManagementService.bulkDeleteQRCodes(selectedCodes);
      setSelectedCodes([]);
      await loadQRCodeData();
    } catch (error) {
      console.error('Error deleting QR codes:', error);
      setError('Failed to delete QR codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteExpired = async () => {
    if (!window.confirm('Are you sure you want to delete all expired QR codes?')) {
      return;
    }

    try {
      setLoading(true);
      const deletedCount = await qrCodeManagementService.deleteExpiredQRCodes();
      alert(`Successfully deleted ${deletedCount} expired QR code(s)`);
      await loadQRCodeData();
    } catch (error) {
      console.error('Error deleting expired QR codes:', error);
      setError('Failed to delete expired QR codes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      await qrCodeManagementService.exportQRCodesToCSV(getFilteredQRCodes());
    } catch (error) {
      console.error('Error exporting QR codes:', error);
      setError('Failed to export QR codes. Please try again.');
    }
  };

  const handleCreatePermanentCode = async (code: string, note: string) => {
    try {
      setLoading(true);
      await qrCodeManagementService.createPermanentQRCode(code, note);
      await loadQRCodeData();
      setShowPermanentModal(false);
      setActiveTab('permanent'); // Switch to permanent tab to see the new code
    } catch (error) {
      console.error('Error creating permanent QR code:', error);
      setError('Failed to create permanent QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivatePermanent = async (id: string) => {
    try {
      await qrCodeManagementService.deactivateQRCode(id);
    } catch (error) {
      console.error('Error deactivating QR code:', error);
      setError('Failed to deactivate QR code. Please try again.');
    }
  };

  const handleReactivatePermanent = async (id: string) => {
    try {
      await qrCodeManagementService.reactivateQRCode(id);
    } catch (error) {
      console.error('Error reactivating QR code:', error);
      setError('Failed to reactivate QR code. Please try again.');
    }
  };

  const handleDeletePermanent = async (id: string) => {
    try {
      await qrCodeManagementService.deletePermanentQRCode(id);
    } catch (error) {
      console.error('Error deleting QR code:', error);
      setError('Failed to delete QR code. Please try again.');
    }
  };

  const handleLoadRedemptions = async (id: string, limit: number, offset: number) => {
    return await qrCodeManagementService.getQRCodeRedemptions(id, limit, offset);
  };

  const getFilteredQRCodes = () => {
    return qrCodes.filter(qr => {
      // Filter by tab (temporary/permanent)
      if (activeTab === 'temporary' && qr.isPermanent) return false;
      if (activeTab === 'permanent' && !qr.isPermanent) return false;

      // Status filter
      if (filterStatus !== 'all' && qr.status !== filterStatus) {
        return false;
      }
      
      // Hotel filter
      if (filterHotel !== 'all' && qr.hotelName !== filterHotel) {
        return false;
      }
      
      // Search filter
      if (searchTerm && !qr.code.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !qr.hotelName?.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      return true;
    });
  };

  const getUniqueHotels = () => {
    const hotels = new Set<string>();
    qrCodes.forEach(qr => {
      if (qr.hotelName) hotels.add(qr.hotelName);
    });
    return Array.from(hotels).sort();
  };

  const filteredQRCodes = getFilteredQRCodes();

  // Render content based on state
  const renderContent = () => {
    if (loading && !qrCodes.length) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading QR code management...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{error}</p>
            <button
              onClick={loadQRCodeData}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return (
      <>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">QR Code Management</h1>
              <p className="mt-2 text-gray-600">
                Manage user activation QR codes with bulk operations and comprehensive tracking
                <br />
                <span className="text-sm font-medium text-blue-600">
                  ✓ Real-time tracking • {qrCodes.length} total codes • Temporary & Permanent codes
                </span>
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowPermanentModal(true)}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                <Star className="h-4 w-4 mr-2" />
                Create Permanent
              </button>

              <button
                onClick={() => setShowBulkCreateModal(true)}
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Bulk Create
              </button>
              
              <button
                onClick={handleDeleteExpired}
                className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Expired
              </button>
              
              <button
                onClick={handleExportCSV}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </button>
              
              <button
                onClick={loadQRCodeData}
                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                disabled={loading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-3" />
            <p className="text-red-800">{error}</p>
            <button 
              onClick={() => setError(null)}
              className="ml-auto text-red-600 hover:text-red-800"
            >
              <XCircle className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* KPI Cards */}
        {stats && (
          <div className="mb-8">
            <QRCodeKPICards
              totalCodes={stats.totalActivationCodes}
              usedCodes={stats.usedActivationCodes}
              availableCodes={stats.availableActivationCodes}
              expiredCodes={stats.expiredActivationCodes}
              activatedUsers={stats.activatedUsers}
              activationRate={stats.activationRate}
            />
            
            {/* Permanent QR Code Stats */}
            {stats.totalPermanentCodes > 0 && (
              <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-6">
                <div className="bg-purple-50 rounded-xl p-6 border-2 border-purple-200">
                  <div className="text-purple-600 text-sm font-medium mb-2">Total Permanent Codes</div>
                  <div className="text-3xl font-bold text-purple-900">{stats.totalPermanentCodes}</div>
                </div>
                <div className="bg-green-50 rounded-xl p-6 border-2 border-green-200">
                  <div className="text-green-600 text-sm font-medium mb-2">Active Permanent</div>
                  <div className="text-3xl font-bold text-green-900">{stats.activePermanentCodes}</div>
                </div>
                <div className="bg-blue-50 rounded-xl p-6 border-2 border-blue-200">
                  <div className="text-blue-600 text-sm font-medium mb-2">Total Redemptions</div>
                  <div className="text-3xl font-bold text-blue-900">{stats.permanentCodeRedemptions}</div>
                </div>
                <div className="bg-indigo-50 rounded-xl p-6 border-2 border-indigo-200">
                  <div className="text-indigo-600 text-sm font-medium mb-2">Unique Users</div>
                  <div className="text-3xl font-bold text-indigo-900">{stats.uniqueUsersForPermanent}</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('temporary')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'temporary' ?'border-blue-500 text-blue-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Temporary Codes ({qrCodes.filter(qr => !qr.isPermanent).length})
              </button>
              <button
                onClick={() => setActiveTab('permanent')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'permanent' ?'border-purple-500 text-purple-600' :'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Permanent Codes ({qrCodes.filter(qr => qr.isPermanent).length})
              </button>
            </nav>
          </div>
        </div>

        {/* Filters and Bulk Actions */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            {/* Search and Filters */}
            <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
              <input
                type="text"
                placeholder="Search codes or hotels..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="redeemed">Redeemed</option>
                <option value="expired">Expired</option>
              </select>
              
              <select
                value={filterHotel}
                onChange={(e) => setFilterHotel(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Hotels</option>
                {getUniqueHotels().map(hotel => (
                  <option key={hotel} value={hotel}>{hotel}</option>
                ))}
              </select>

              {/* Items per page selector */}
              <select
                value={activeTab === 'temporary' ? tempItemsPerPage : itemsPerPage}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  if (activeTab === 'temporary') {
                    setTempItemsPerPage(newValue);
                    setTempCurrentPage(1);
                  } else {
                    setItemsPerPage(newValue);
                    setCurrentPage(1);
                  }
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={20}>20 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
            </div>

            {/* Bulk Actions for temporary codes */}
            {activeTab === 'temporary' && selectedCodes.length > 0 && (
              <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">
                  {selectedCodes.length} selected
                </span>
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected
                </button>
              </div>
            )}
          </div>
        </div>

        {/* QR Codes Table - Conditional rendering based on tab */}
        {activeTab === 'temporary' ? (
          <QRCodeDataTable
            qrCodes={filteredQRCodes}
            selectedCodes={selectedCodes}
            onSelectionChange={setSelectedCodes}
            onRefresh={loadQRCodeData}
            itemsPerPage={tempItemsPerPage}
            currentPage={tempCurrentPage}
            onPageChange={setTempCurrentPage}
          />
        ) : (
          <PermanentQRCodeTable
            qrCodes={filteredQRCodes}
            onRefresh={loadQRCodeData}
            onDeactivate={handleDeactivatePermanent}
            onReactivate={handleReactivatePermanent}
            onDelete={handleDeletePermanent}
            onLoadRedemptions={handleLoadRedemptions}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={setCurrentPage}
          />
        )}

        {/* Summary Stats */}
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filter Summary</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{filteredQRCodes.length}</div>
              <div className="text-sm text-gray-600">Matching Codes</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {filteredQRCodes.filter(qr => qr.status === 'active').length}
              </div>
              <div className="text-sm text-gray-600">Active</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600">
                {filteredQRCodes.filter(qr => qr.status === 'redeemed').length}
              </div>
              <div className="text-sm text-gray-600">Redeemed</div>
            </div>
            
            <div className="text-center">
              <div className="text-2xl font-bold text-orange-600">
                {filteredQRCodes.filter(qr => qr.status === 'expired').length}
              </div>
              <div className="text-sm text-gray-600">Expired</div>
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <Helmet>
        <title>QR Code Management - TravelHeroes Admin Dashboard</title>
        <meta name="description" content="QR Code activation management and tracking dashboard" />
      </Helmet>

      <div className="p-6">
        {renderContent()}
        
        {/* Bulk Create Modal */}
        {showBulkCreateModal && (
          <BulkCreateModal
            onClose={() => setShowBulkCreateModal(false)}
            onCreate={handleBulkCreate}
          />
        )}

        {/* Permanent QR Code Modal */}
        {showPermanentModal && (
          <PermanentQRCodeModal
            onClose={() => setShowPermanentModal(false)}
            onCreate={handleCreatePermanentCode}
          />
        )}
      </div>
    </>
  );
};

export default QRCodeManagement;